export const DEFAULT_COUNTRY_CODE = "bd"
export const DEFAULT_COUNTRY_PREFIX = `/${DEFAULT_COUNTRY_CODE}`

const trimTrailingSlash = (value: string) =>
  value.length > 1 ? value.replace(/\/+$/, "") : value

const isRelativeOnly = (value: string) =>
  value.startsWith("?") || value.startsWith("#")

const splitSuffix = (value: string) => {
  const queryIndex = value.indexOf("?")
  const hashIndex = value.indexOf("#")
  const indexes = [queryIndex, hashIndex].filter((index) => index >= 0)
  const suffixIndex = indexes.length > 0 ? Math.min(...indexes) : -1

  if (suffixIndex < 0) {
    return { pathname: value, suffix: "" }
  }

  return {
    pathname: value.slice(0, suffixIndex),
    suffix: value.slice(suffixIndex),
  }
}

export function isDefaultCountryPath(pathname: string): boolean {
  const { pathname: pathOnly } = splitSuffix(pathname)

  return (
    pathOnly === DEFAULT_COUNTRY_PREFIX ||
    pathOnly.startsWith(`${DEFAULT_COUNTRY_PREFIX}/`)
  )
}

export function stripDefaultCountryPath(pathname: string): string {
  if (!isDefaultCountryPath(pathname)) {
    return normalizePublicPath(pathname)
  }

  const stripped = pathname.slice(DEFAULT_COUNTRY_PREFIX.length)
  if (stripped.startsWith("?") || stripped.startsWith("#")) {
    return `/${stripped}`
  }

  return normalizePublicPath(stripped || "/")
}

export function normalizePublicPath(pathname?: string | null): string {
  const raw = String(pathname || "/").trim()
  if (!raw || raw === "/") return "/"
  if (isRelativeOnly(raw)) return raw
  if (/^https?:\/\//i.test(raw)) return raw

  const { pathname: rawPathname, suffix } = splitSuffix(raw)
  const withSlash = rawPathname.startsWith("/") ? rawPathname : `/${rawPathname}`
  const withoutDefaultCountry = isDefaultCountryPath(withSlash)
    ? withSlash.slice(DEFAULT_COUNTRY_PREFIX.length) || "/"
    : withSlash
  const normalized = withoutDefaultCountry.startsWith("/")
    ? withoutDefaultCountry
    : `/${withoutDefaultCountry}`

  return `${trimTrailingSlash(normalized)}${suffix}`
}

export function publicPathFromInternalPath(pathname: string): string {
  return stripDefaultCountryPath(pathname)
}

export function internalPathForPublicPath(pathname: string): string {
  const publicPath = normalizePublicPath(pathname)
  return publicPath === "/"
    ? DEFAULT_COUNTRY_PREFIX
    : `${DEFAULT_COUNTRY_PREFIX}${publicPath}`
}

export function canonicalUrl(
  pathname: string,
  baseUrl = process.env.NEXT_PUBLIC_BASE_URL || ""
): string {
  const base = (baseUrl || "").replace(/\/+$/, "")
  const path = normalizePublicPath(pathname)

  if (!base) return path
  return path === "/" ? `${base}/` : `${base}${path}`
}
